// 攝影機「要不要 PIN」（John 2026-10-21）：家人只看到一個開關「🔒 要 PIN 才能看 / 🔓 面板上直接看」，
// 不再露出室內 / 室外 / 門口。底層照舊 location-tag（panels.ts cameraPinTag）。
//   - CameraPinToggle：勾選的攝影機下面一行開關 + 一句說明
//   - CameraPinAsk：從沒標過的攝影機第一次放進面板時當場問一次（關掉不選 = 維持沒標 = 要 PIN）
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../../lib/useFocusTrap';
import { cameraPinTag, cameraRequiresPin } from './panels';

export type CameraLoc = '' | 'indoor' | 'outdoor' | 'door';

export function CameraPinToggle({ deviceId, location, onChange }: { deviceId: string; location: CameraLoc; onChange: (loc: CameraLoc) => void }) {
  const { t } = useTranslation();
  const pin = cameraRequiresPin(location);
  return (
    <div className="dv-cam-pin" data-testid={`cam-pin-${deviceId}`}>
      <label className="dv-check">
        <input type="checkbox" role="switch" checked={pin} aria-checked={pin} data-testid={`cam-pin-switch-${deviceId}`}
          onChange={() => onChange(cameraPinTag(!pin, location))} />
        {pin ? t('panels.camPin.required') : t('panels.camPin.direct')}
      </label>
      <span className="text-secondary">{t('panels.camPin.hint')}</span>
    </div>
  );
}

/** 「這台拍的是家裡面嗎？」是 → indoor；不是 → outdoor；Esc / 點外面 → onDismiss（不寫，維持要 PIN） */
export function CameraPinAsk({ name, onAnswer, onDismiss }: { name: string; onAnswer: (loc: 'indoor' | 'outdoor') => void; onDismiss: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(true, ref);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);
  return (
    <div ref={ref} role="dialog" aria-modal="true" aria-label={t('panels.camPin.askTitle')} data-testid="cam-pin-ask"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: 'var(--spacing-8)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}>
      <div className="card" style={{ width: 'min(420px, 100%)', display: 'grid', gap: 'var(--spacing-5)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{t('panels.camPin.askTitle')}</h3>
        <div className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
          <strong>{name}</strong>
          <p style={{ margin: 'var(--spacing-2) 0 0' }}>{t('panels.camPin.askBody')}</p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-4)', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn-ghost" data-autofocus data-testid="cam-pin-ask-no" onClick={() => onAnswer('outdoor')}>{t('panels.camPin.askNo')}</button>
          <button className="btn-primary" data-testid="cam-pin-ask-yes" onClick={() => onAnswer('indoor')}>{t('panels.camPin.askYes')}</button>
        </div>
      </div>
    </div>
  );
}
